import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useStoreState } from '~/store/hooks';

interface IResult {
  alertMessage: string | null;
  noteMessage: string | null;
  locationGroupIsSelectedNote: string | null;
  clientIsSelectedNote: string | null;
}

interface UseClientAvailabilityMessagesProps {
  clientId: number;
  memberId: number;
  teamMemberIdx: number;
}

const useClientAvailabilityMessages = ({
  clientId,
  memberId,
  teamMemberIdx,
}: UseClientAvailabilityMessagesProps): IResult => {
  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const selectedLocationGroupClientIds = useWatch({
    name: `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupClientIds`,
  });
  const selectedClientsIds = useWatch({
    name: `teamMembersAllocations.${teamMemberIdx}.selectedClientsIds`,
  });

  const locationGroupIsSelectedNote = useMemo(() => {
    const isClientAlreadyAddedGroupName = selectedLocationGroupClientIds[clientId];

    if (selectedLocationGroupClientIds[clientId]) {
      return `This user already has allocations created by ${isClientAlreadyAddedGroupName}. Please use the same grouping type to edit this user's allocation.`;
    }
    return null;
  }, [selectedLocationGroupClientIds, clientId]);

  const clientIsSelectedNote = useMemo(() => {
    const isClientAlreadyAdded = selectedClientsIds.includes(clientId);

    if (isClientAlreadyAdded) {
      return `This user already has allocations created by ${selectedClientsIds}. Please use the same grouping type to edit this user's allocation.`;
    }

    return null;
  }, [selectedClientsIds, clientId]);

  const alertMessage = useMemo(() => {
    if (!dateAvailability?.isValid) {
      const match = dateAvailability?.clients.find(
        client =>
          client.clientId === clientId && !client.isDifferentUser && client.userId === memberId,
      );
      if (match) {
        return `Alert: client is already allocated to this user for this date${
          match.isDifferentTeam ? ' on another team' : ''
        }. Please remove in order to save.`;
      }
    }
    return null;
  }, [dateAvailability, clientId, memberId]);

  const noteMessage = useMemo(() => {
    if (!dateAvailability?.isValid) {
      const match = dateAvailability?.clients.find(
        client =>
          client.clientId === clientId && client.isDifferentUser && client.userId === memberId,
      );
      if (match) {
        return `Note: this client is allocated on this date${
          match.isDifferentTeam ? ' for another team.' : '.'
        }`;
      }
    }
    return null;
  }, [dateAvailability, clientId, memberId]);

  return { alertMessage, noteMessage, locationGroupIsSelectedNote, clientIsSelectedNote };
};

export default useClientAvailabilityMessages;
